import { ActionBulkModalAiLookalike } from '@aibees/elephant/components/action-bulk-modal-ai-lookalike';
import { AssignOwner } from '@aibees/elephant/components/assign-owner';
import { CreateActionBulkDialog } from '@aibees/elephant/components/create-action-bulk-dialog';
import { ListLabelActionBulkModal } from '@aibees/elephant/components/list-label-action-bulk-modal';
import { useLabelDialogConfig } from '@aibees/elephant/hooks/labels/use-label-dialog-config';
import { LabelType } from '@aibees/elephant/types/label';
import {
  DialogActions,
  DialogTitle,
  Form,
  FormOnSubmit,
} from '@aibees/shared/components';
import { useBoolean } from '@aibees/shared/hooks';
import { Dialog, DialogContent, Fade } from '@mui/material';
import { memo, useCallback } from 'react';
import { config, scheme } from './config';
import type { SearchTableActionAddLabelDialogProps as Props } from './types';
import { useAddLabelDialog } from './use-add-label-dialog';

const stopPropagation = (e: React.MouseEvent<HTMLDivElement>) =>
  e.stopPropagation();
const DIALOG_PAPER_PROPS = { onMouseDown: stopPropagation };

let { defaultValues } = config;

export const SearchTableActionAddLabelDialog = memo(({ className }: Props) => {
  // Custom hook for add label dialog logic
  const {
    labelDialogState,
    ids,
    labels,
    expandedAccordion,
    showAiLookalike,
    workspace,
    handleAddToLabel,
    handleClose,
    handleDialogClose,
    setExpandedAccordion,
    grouping,
  } = useAddLabelDialog();

  // Local state
  const loading = useBoolean(false);
  const state = useBoolean(false);
  const { titleDialog, inputTitle, placeholder } = useLabelDialogConfig({
    isLabel: true,
  });

  // Memoize handleSubmit to prevent recreation on each render
  const handleSubmit: FormOnSubmit = useCallback(
    (data) => {
      handleAddToLabel(data, {
        startProgress: loading.onTrue,
        endProgress: loading.onFalse,
        toggle: () => handleClose('label'),
      });
    },
    [handleAddToLabel, loading.onTrue, loading.onFalse, handleClose]
  );

  defaultValues ||= {};

  return (
    <Form
      id="add-label-dialog"
      scheme={scheme}
      loading={loading.value}
      customSubmitButton
      defaultValues={defaultValues}
      onSubmit={handleSubmit}
    >
      <Dialog
        open={labelDialogState.value}
        fullWidth={true}
        maxWidth="xs"
        onClose={handleDialogClose}
        TransitionComponent={Fade}
        PaperProps={DIALOG_PAPER_PROPS}
      >
        <DialogTitle
          title="Add to Label"
          icon="ic-new-label"
          onClose={handleDialogClose}
        />

        <DialogContent padding="none">
          <ListLabelActionBulkModal
            type={LabelType.LABEL}
            state={state}
            firstState={labelDialogState}
            optional={false}
            expandedAccordion={expandedAccordion}
            setExpandedAccordion={setExpandedAccordion}
          />

          {workspace && (
            <AssignOwner
              expandedAccordion={expandedAccordion}
              setExpandedAccordion={setExpandedAccordion}
            />
          )}

          <ActionBulkModalAiLookalike
            expandedAccordion={expandedAccordion}
            setExpandedAccordion={setExpandedAccordion}
          />
        </DialogContent>

        <DialogActions
          formId="add-label-dialog"
          disabled={!labels.length}
          onClose={handleDialogClose}
        />
      </Dialog>

      <CreateActionBulkDialog
        state={state}
        firstState={labelDialogState}
        title={titleDialog}
        type={LabelType.LABEL}
        inputTitle={inputTitle}
        hasColor={true}
        hasDescription={true}
        placeholder={placeholder}
        grouping={grouping}
      />
    </Form>
  );
});

SearchTableActionAddLabelDialog.displayName = 'SearchTableActionAddLabelDialog';
